import { Sector, Split } from "../models/Common";
import { IDashboardData, IGlobalSummary } from "../models/Dashboard";
import { FundItem, IFundCarousel } from "../models/Fund";
import { FundAffectation, PortfolioAffectation, PortfolioCreateItem, PortfolioDetailsItem, PortfolioIncorporation, PortfolioLibraryCount, PortfolioLibraryItem, PortfolioStatus, PortfolioTracking } from "../models/Portfolio";
import { LLeoAPIFundCarouselEndpoint, LeoAPIDashboardDataEndpoint, LeoAPIDashboardStatisticEndpoint, LeoAPIDashboardUrl, LeoAPIFundUrl, LeoAPIPortfolioAffectEndpoint, LeoAPIPortfolioCountEndpoint, LeoAPIPortfolioEditEndpoint, LeoAPIPortfolioFundEndpoint, LeoAPIPortfolioIncorporateEndpoint, LeoAPIPortfolioLibraryUrl, LeoAPIPortfolioSubmitEndpoint, LeoAPIPortfolioTrackingEndpoint, LeoAPIPortfolioUrl, LeoAPIProfileFavoritesEndpoint, LeoAPIProfileUrl, LeoAPISectorsUrl, LeoAPISplitsUrl } from "../resources/api-constants";
import axios from "../utility/customAxios";

export interface ILeoService {
    GetGlobalStatistics(): Promise<IGlobalSummary>;
    GetUserStatistics(): Promise<IDashboardData[]>;
    GetPorfolioLibraryItems(status?: PortfolioStatus): Promise<PortfolioLibraryItem[]>;
    GetPorfolioLibraryCounts(): Promise<PortfolioLibraryCount[]>;
    GetPortfolioDetails(id: string): Promise<PortfolioDetailsItem>;
    DeletePortfolioItem(id: string): Promise<void>;
}

export class LeoService implements ILeoService {
    public async GetGlobalStatistics(): Promise<IGlobalSummary> {
        return await axios.get(LeoAPIDashboardUrl + LeoAPIDashboardStatisticEndpoint, {
            headers: {
                Accept: "*/*"
            }
        }).then(r => r.data);
    }

    public async GetUserStatistics(): Promise<IDashboardData[]> {
        return await axios.get(LeoAPIDashboardUrl + LeoAPIDashboardDataEndpoint, {
            headers: {
                Accept: "*/*"
            }
        }).then(r => r.data);
    }

    /* ********** Portfolios ********** */

    public async GetPorfolioLibraryItems(status?: PortfolioStatus): Promise<PortfolioLibraryItem[]> {

        return await axios.get(LeoAPIPortfolioLibraryUrl + (status !== undefined ? `?status=${status}` : ''), {
            headers: {
                Accept: "*/*"
            }
        }).then(r => r.data.map((i: any): PortfolioLibraryItem => { return { ...i, closingDate: new Date(i.closingDate) }; }));
    }

    public async GetPorfolioLibraryCounts(): Promise<PortfolioLibraryCount[]> {
        return await axios.get(LeoAPIPortfolioLibraryUrl + LeoAPIPortfolioCountEndpoint, {
            headers: {
                Accept: "*/*"
            }
        }).then(r => r.data);
    }

    public async GetPortfolioDetails(id: string): Promise<PortfolioDetailsItem> {
        return await axios.get(LeoAPIPortfolioUrl + `?id=${id}`, {
            headers: {
                Accept: "*/*"
            }
        }).then(r => ({
            ...r.data,
            closingDate: new Date(r.data.closingDate),
            reports: r.data.reports.map((r: any) => ({ ...r, date: new Date(r.date) })),
            funds: r.data.funds.map((f: any) => ({ ...f, reports: f.reports.map((r: any) => ({ ...r, date: new Date(r.date) })) }))
        }));
    }

    public async DeletePortfolioItem(id: string): Promise<void> {
        return await axios.delete(LeoAPIPortfolioUrl + `?id=${id}`, {
            headers: {
                Accept: "*/*"
            }
        });
    }
    
    public async AddFundToPortfolio(id: string, fundId: number): Promise<void> {
        return await axios.put(LeoAPIPortfolioUrl + LeoAPIPortfolioFundEndpoint + `?id=${id}&fundid=${fundId}`, {}, {
            headers: {
                Accept: "*/*"
            },
        });
    }
    
    public async RemoveFundFromPortfolio(id: string, fundId: number): Promise<void> {
        return await axios.delete(LeoAPIPortfolioUrl + LeoAPIPortfolioFundEndpoint + `?id=${id}&fundid=${fundId}`, {
            headers: {
                Accept: "*/*"
            },
        });
    }
    
    public async UpdateFundOfPortfolio(id: string, affectation: FundAffectation): Promise<void> {
        return await axios.post(LeoAPIPortfolioUrl + LeoAPIPortfolioFundEndpoint,{
            portfolioId: id,
            fundId: affectation.id,
            proportion: affectation.proportion
        }, {
            headers: {
                Accept: "*/*"
            },
        });
    }

    public async CreatePortfolio(name: string): Promise<string>{
        return await axios.put(LeoAPIPortfolioUrl, { name: name}, {
            headers: {
                Accept: "*/*"
            },
        }).then(r => r.data)
    }
    
    public async GetEditPortfolio(id: string): Promise<PortfolioCreateItem> {
        return await axios.get(LeoAPIPortfolioUrl + LeoAPIPortfolioEditEndpoint + `?id=${id}`, {
            headers: {
                Accept: "*/*"
            }
        }).then(r => ({
            id: r.data.id,
            name: r.data.name,
            fundraisingTarget: r.data.fundRaisingTarget,
            sectors: r.data.sectors.map((s: any) => ({ id: s.tagId, proportion: s.proportion})),
            countries: r.data.countries.map((s: any) => ({ id: s.tagId, proportion: s.proportion})),
            splits: r.data.splits.map((s: any) => ({ id: s.tagId, proportion: s.proportion})),
            stage: { early: r.data.stageEarly, growth: r.data.stageGrowth, late: r.data.stageLate },
            funds: r.data.funds.map((s: any) => ({ id: s.id, proportion: s.proportion})),
            fundCount: r.data.numberOfFunds === undefined ? NaN : r.data.numberOfFunds,
            managementFees: r.data.managementFees,
            carryFees: r.data.carryFees,
            portfolioLife: r.data.portfolioLife,
            multipleAtExit: r.data.multipleAtExit,
            step: r.data.step,
            accreditedInvestors: r.data.accreditedInvestors,
            retailInvestors: r.data.retailInvestors
        }));
    }

    public async UpdatePortfolio(portfolio: PortfolioCreateItem): Promise<void>{
        return await axios.post(LeoAPIPortfolioUrl, {
            id: portfolio.id,
            name: portfolio.name,
            fundRaisingTarget: portfolio.fundraisingTarget,
            // fundRaisingTargetMax: portfolio.fundraisingTarget[1],
            // fundRaisingTargetMin: portfolio.fundraisingTarget[0],
            numberOfFunds: isNaN(portfolio.fundCount) ? undefined : portfolio.fundCount,
            stageEarly: portfolio.stage.early,
            stageGrowth: portfolio.stage.growth,
            stageLate: portfolio.stage.late,
            sectors: portfolio.sectors.map(s => ({ tagId: s.id, proportion: s.proportion})),
            countries: portfolio.countries.map(c => ({ tagId: c.id, proportion: c.proportion})),
            splits: portfolio.splits.map(s => ({ tagId: s.id, proportion: s.proportion})),
            managementFees: portfolio.managementFees,
            carryFees: portfolio.carryFees,
            portfolioLife: portfolio.portfolioLife,
            multipleAtExit: portfolio.multipleAtExit,
            step: portfolio.step,
            accreditedInvestors: portfolio.accreditedInvestors,
            retailInvestors: portfolio.retailInvestors
        }, {
            headers: {
                Accept: "*/*"
            },
        });
    }

    public async SubmitPortoflio(id: string): Promise<void>{
        return await axios.post(LeoAPIPortfolioUrl + LeoAPIPortfolioSubmitEndpoint, {
            id: id
        }, {
            headers: {
                Accept: "*/*"
            },
        });
    }

    public async IncorporatePortfolio(id: string, incorporate: PortfolioIncorporation): Promise<void>{
        return await axios.post(LeoAPIPortfolioUrl + LeoAPIPortfolioIncorporateEndpoint, {
            id: id,
            incorporationType: incorporate
        }, {
            headers: {
                Accept: "*/*"
            },
        });
    }

    public async AffectPortfolio(id: string, affectation: PortfolioAffectation): Promise<void>{
        return await axios.post(LeoAPIPortfolioUrl + LeoAPIPortfolioAffectEndpoint, {
            id: id,
            affectation: affectation
        }, {
            headers: {
                Accept: "*/*"
            },
        });
    }

    public async GetPortfolioTracking(id: string): Promise<PortfolioTracking> {
        return await axios.get(LeoAPIPortfolioUrl + LeoAPIPortfolioTrackingEndpoint + `?id=${id}`, {
            headers: {
                Accept: "*/*"
            }
        }).then(r => ({...r.data, events: [...r.data.events.map((e: any) => ({...e, eventTime: new Date(e.eventTime)}))]}));
    }

    /* ********** Funds ********** */
    public async GetFunds(): Promise<FundItem[]> {

        return await axios.get(LeoAPIFundUrl, {
            headers: {
                Accept: "*/*"
            }
        }).then(r => r.data.map((i: any): FundItem => {
            return {
                ...i,
                closingDate: new Date(i.closingDate),
                publicationDate: new Date(i.publicationDate),
                dataRooms: i.dataRooms.map((d: any) => ({ ...d, date: new Date(d.date) }))
            };
        }));
    }

    public async GetCarousel(): Promise<IFundCarousel>{
        return await axios.get(LeoAPIFundUrl + LLeoAPIFundCarouselEndpoint,{
            headers: {
                Accept: "*/*"
            }
        }).then(r => r.data);
    }

    /* ********** Profile ********** */
    public async GetFavorites(): Promise<number[]> {
        return await axios.get(LeoAPIProfileUrl + LeoAPIProfileFavoritesEndpoint, {
            headers: {
                Accept: "*/*"
            }
        }).then(r => r.data);
    }

    public async AddFavorites(fundId: number): Promise<void> {
        return await axios.post(LeoAPIProfileUrl + LeoAPIProfileFavoritesEndpoint , {fundId: fundId}, {
            headers: {
                Accept: "*/*"
            }
        });
    }

    public async DeleteFavorites(fundId: number): Promise<void> {
        return await axios.delete(LeoAPIProfileUrl + LeoAPIProfileFavoritesEndpoint + `?fundId=${fundId}`, {
            headers: {
                Accept: "*/*"
            }
        });
    }

    /* ********** Common ********** */
    public async GetSectors(): Promise<Sector[]> {
        return await axios.get(LeoAPISectorsUrl, {
            headers: {
                Accept: "*/*"
            }
        }).then(r => r.data);
    }
    public async GetSplits(): Promise<Split[]> {
        return await axios.get(LeoAPISplitsUrl, {
            headers: {
                Accept: "*/*"
            }
        }).then(r => r.data);
    }
}